import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { clsx } from 'clsx';
import {
  IpponButton,
  IpponCheckbox,
  IpponHSpace,
  IpponHSpaceSlot,
  IpponIon,
  IpponText,
  IpponVSpace,
} from '@ippon-ui/react';
import { type Page, PaginationComponent } from '@/generic/pagination';
import type { DraftRule, DraftRuleEdit } from '@/rules-management/domain/DraftRule.ts';
import type { DraftRulesSelection } from '@/rules-management/domain/DraftRulesSelection.ts';
import {
  DraftRuleDetailsComponent,
  DraftRuleHeadingComponent,
} from '@/rules-management/primary/DraftRuleComponent.tsx';
import { DraftRuleEditComponent } from '@/rules-management/primary/DraftRuleEditComponent.tsx';

const IncompleteDraftRuleComponent = () => {
  const { t } = useTranslation('rules-management');

  return (
    <IpponText
      variant="body"
      size="small"
      color="neutral-tertiary-inversed"
      dataSelector="rules-management.rules.import.result.rule.incomplete"
    >
      {t('rules-management.rules.import.result.incomplete')}
    </IpponText>
  );
};

const DraftRuleToggleComponent = ({
  draft,
  selected,
  onToggle,
  onDiscard,
  onSave,
}: {
  draft: DraftRule;
  selected: boolean;
  onToggle: () => void;
  onDiscard: () => void;
  onSave: (edit: DraftRuleEdit) => Promise<boolean>;
}) => {
  const [shown, setShown] = useState(false);
  const [editing, setEditing] = useState(false);

  const toggle = () => setShown((shown) => !shown);
  const id = `draft-rule-${draft.id.value}`;

  const startEdit = () => {
    setEditing(true);
    setShown(true);
  };

  const cancelEdit = () => setEditing(false);

  const save = (edit: DraftRuleEdit): Promise<boolean> =>
    onSave(edit).then((success) => {
      if (success) {
        setEditing(false);
      }
      return success;
    });

  return (
    <div
      className={clsx('ippon-toggle', { '-open': shown })}
      data-selector="rules-management.rules.import.result.rule"
    >
      <div className="ippon-toggle--header">
        <button
          type="button"
          className="ippon-toggle--button"
          data-selector="rules-management.rules.import.result.rule.toggle"
          onClick={toggle}
          id={id}
          aria-expanded={shown}
        >
          <IpponIon name="chevron-forward" className="ippon-toggle--icon" />
          <DraftRuleHeadingComponent draft={draft} />
        </button>
        {!editing && (
          <div className="ippon-toggle--actions">
            {!draft.isPublishable && <IncompleteDraftRuleComponent />}
            <IpponButton
              color="neutral"
              variant="text"
              onClick={startEdit}
              iconLeft={{ name: 'pencil', variant: 'outline' }}
              dataSelector="rules-management.rules.import.result.rule.edit"
            />
            <IpponButton
              color="error"
              variant="text"
              onClick={onDiscard}
              iconLeft={{ name: 'trash', variant: 'outline' }}
              dataSelector="rules-management.rules.import.result.rule.discard"
            />
            <IpponCheckbox
              id={`${id}-checkbox`}
              checked={selected}
              disabled={!draft.isPublishable}
              onChange={onToggle}
              dataSelector="rules-management.rules.import.result.rule.checkbox"
            />
          </div>
        )}
      </div>
      <section className="ippon-toggle--body" aria-labelledby={id}>
        <div className="ippon-toggle--content">
          <IpponVSpace gap={8}>
            {editing ? (
              <DraftRuleEditComponent draft={draft} onSave={save} onCancel={cancelEdit} />
            ) : (
              <DraftRuleDetailsComponent draft={draft} />
            )}
          </IpponVSpace>
        </div>
      </section>
    </div>
  );
};

const SelectAllDraftRulesComponent = ({
  drafts,
  selection,
  onSelectionChange,
}: {
  drafts: DraftRule[];
  selection: DraftRulesSelection;
  onSelectionChange: (selection: DraftRulesSelection) => void;
}) => {
  const { t } = useTranslation('rules-management');

  const allSelected = selection.holdsAllOf(drafts);
  const partiallySelected = selection.holdsPartOf(drafts);
  const hasPublishableDraft = drafts.some((draft) => draft.isPublishable);

  const toggleAll = () =>
    onSelectionChange(allSelected ? selection.deselectAll(drafts) : selection.selectAll(drafts));

  const markPartialSelection = (input: HTMLInputElement | null) => {
    if (input) {
      input.indeterminate = partiallySelected;
    }
  };

  return (
    <IpponCheckbox
      id="draft-rules-select-all"
      ref={markPartialSelection}
      checked={allSelected}
      disabled={!hasPublishableDraft}
      onChange={toggleAll}
      dataSelector="rules-management.rules.import.result.select-all"
    >
      {t('rules-management.rules.import.result.select-all')}
    </IpponCheckbox>
  );
};

export const DraftRulesContent = ({
  drafts,
  selection,
  onSelectionChange,
  onPageChange,
  onDiscard,
  onSave,
}: {
  drafts: Page<DraftRule>;
  selection: DraftRulesSelection;
  onSelectionChange: (selection: DraftRulesSelection) => void;
  onPageChange: (page: number) => void;
  onDiscard: (draft: DraftRule, page: number) => void;
  onSave: (draft: DraftRule, edit: DraftRuleEdit, page: number) => Promise<boolean>;
}) => {
  const { t } = useTranslation('rules-management');

  return (
    <>
      <div className="ippon-modal--actions">
        <IpponHSpace gap={24} align="middle">
          <IpponHSpaceSlot expand={true}>
            <SelectAllDraftRulesComponent
              drafts={drafts.content}
              selection={selection}
              onSelectionChange={onSelectionChange}
            />
          </IpponHSpaceSlot>
          <IpponText
            variant="body"
            size="small"
            color="neutral-tertiary-inversed"
            dataSelector="rules-management.rules.import.result.count"
          >
            {t('rules-management.rules.import.result.count', { count: drafts.totalElementsCount })}
          </IpponText>
        </IpponHSpace>
      </div>
      <div className="ippon-modal--body">
        <IpponVSpace gap={16}>
          {drafts.content.map((draft) => (
            <DraftRuleToggleComponent
              draft={draft}
              key={draft.id.value}
              selected={selection.holds(draft)}
              onToggle={() => onSelectionChange(selection.toggle(draft))}
              onDiscard={() => onDiscard(draft, drafts.currentPage)}
              onSave={(edit) => onSave(draft, edit, drafts.currentPage)}
            />
          ))}
          <PaginationComponent
            currentPage={drafts.currentPage}
            totalPages={drafts.totalPages}
            onPageChange={onPageChange}
            dataSelector="rules-management.rules.import.result.pagination"
          />
        </IpponVSpace>
      </div>
    </>
  );
};
